/*
  Isekaid Cinematic Scroll System
  File: assets/isekaid-scroll-reveal.js
*/

(() => {
  const READY_CLASS = 'isekaid-motion-ready';
  const ITEM_CLASS = 'isekaid-scroll-item';
  const IN_VIEW_CLASS = 'is-in-view';

  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  const trackedElements = new Set();

  let frameRequested = false;
  let systemStarted = false;

  /*
    These areas should not receive decorative motion.
  */
  const EXCLUDED_SELECTOR = [
    'header',
    'footer',
    'dialog',
    '[role="dialog"]',
    '.menu-drawer',
    '.cart-drawer',
    '.predictive-search',
    '.shopify-section-group-header-group',
    '.shopify-section-group-footer-group',
    '.isekaid-no-motion'
  ].join(',');

  const isExcluded = (element) => {
    if (!element || !(element instanceof Element)) {
      return true;
    }

    return Boolean(element.closest(EXCLUDED_SELECTOR));
  };

  /*
    Apply one or more classes without repeatedly decorating
    the same element.
  */
  const register = (element, ...classes) => {
    if (!element || isExcluded(element)) {
      return;
    }

    element.classList.add(ITEM_CLASS, ...classes);
    trackedElements.add(element);
  };

  /*
    Calculates the element's relationship to the viewport.

    progress:
      0 = entering from bottom
      0.5 = around viewport center
      1 = leaving through top

    center:
      -1 = below viewport center
       0 = at viewport center
       1 = above viewport center
  */
  const calculatePosition = (element) => {
    const rect = element.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight ||
      document.documentElement.clientHeight;

    const elementCenter = rect.top + rect.height / 2;
    const viewportCenter = viewportHeight / 2;

    const travelRange =
      viewportHeight / 2 + rect.height / 2;

    const rawCenter =
      (viewportCenter - elementCenter) / travelRange;

    const center = Math.max(-1, Math.min(1, rawCenter));
    const progress = (center + 1) / 2;

    const visible =
      rect.bottom > 0 &&
      rect.top < viewportHeight;

    return {
      center,
      progress,
      visible
    };
  };

  /*
    Writes CSS variables for every tracked visible item.
  */
  const updateMotion = () => {
    frameRequested = false;

    if (reducedMotion.matches) {
      trackedElements.forEach((element) => {
        element.classList.add(IN_VIEW_CLASS);
        element.style.removeProperty(
          '--isekaid-scroll-progress'
        );
        element.style.removeProperty(
          '--isekaid-scroll-center'
        );
        element.style.removeProperty(
          '--isekaid-scroll-visible'
        );
      });

      return;
    }

    trackedElements.forEach((element) => {
      if (!element.isConnected) {
        trackedElements.delete(element);
        return;
      }

      const {
        center,
        progress,
        visible
      } = calculatePosition(element);

      element.style.setProperty(
        '--isekaid-scroll-progress',
        progress.toFixed(4)
      );

      element.style.setProperty(
        '--isekaid-scroll-center',
        center.toFixed(4)
      );

      element.style.setProperty(
        '--isekaid-scroll-visible',
        visible ? '1' : '0'
      );

      /*
        Entrance effects replay after the element has
        substantially left the viewport.
      */
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const entranceElement =
        element.classList.contains('isekaid-entrance') ||
        element.classList.contains('isekaid-entrance-left') ||
        element.classList.contains('isekaid-entrance-right');

      if (entranceElement) {
        const shouldShow =
          rect.top < viewportHeight * 0.88 &&
          rect.bottom > viewportHeight * 0.08;

        const farOutside =
          rect.bottom < -120 ||
          rect.top > viewportHeight + 180;

        if (shouldShow) {
          element.classList.add(IN_VIEW_CLASS);
        } else if (farOutside) {
          element.classList.remove(IN_VIEW_CLASS);
        }
      }
    });
  };

  /*
    Limit updates to one animation frame, regardless of how
    many scroll events occur.
  */
  const requestMotionUpdate = () => {
    if (frameRequested) {
      return;
    }

    frameRequested = true;
    window.requestAnimationFrame(updateMotion);
  };

  /*
    Add animation behavior to Horizon and our custom sections.
  */
  const decoratePage = (scope = document) => {
    if (!scope?.querySelectorAll) {
      return;
    }

    /*
      Major Shopify sections receive a slight continuous shift.
      The first visible hero section is excluded from the entrance
      fade so the page never initially appears blank.
    */

    /*
      Section headings.
    */
    scope
      .querySelectorAll(
        [
          'main h1',
          'main h2',
          '.title',
          '.section-heading',
          '.isekaid-creator-carousel__heading',
          '.isekaid-hotspots__heading'
        ].join(',')
      )
      .forEach((heading) => {
        register(
          heading,
          'isekaid-scroll-heading',
          'isekaid-entrance'
        );
      });

    /*
      Supporting copy.
    */
    scope
      .querySelectorAll(
        [
          '.subtitle',
          '.section-subheading',
          '.isekaid-creator-carousel__subheading',
          '.isekaid-hotspots__subheading'
        ].join(',')
      )
      .forEach((text) => {
        register(text, 'isekaid-entrance');
      });

    /*
      Large media areas.

      These selectors deliberately target wrappers so the image
      can move inside a clipped region without changing layout.
    */
    scope
      .querySelectorAll(
        [
          '.banner__media',
          '.image-with-text__media',
          '.slideshow__media',
          '.hero__media',
          '.media-wrapper',
          '.isekaid-hotspots__image-stage'
        ].join(',')
      )
      .forEach((media) => {
        register(
          media,
          'isekaid-scroll-media',
          'isekaid-entrance'
        );
      });

    /*
      Common Horizon media wrappers that contain an actual image.
      Only use them if they are reasonably large, so tiny icons
      do not receive parallax.
    */
    scope
      .querySelectorAll('.media')
      .forEach((media) => {
        const rect = media.getBoundingClientRect();

        if (
          rect.width >= 280 &&
          rect.height >= 240
        ) {
          register(
            media,
            'isekaid-scroll-media',
            'isekaid-entrance'
          );
        }
      });

    /*
      Product cards.
    */
    scope
      .querySelectorAll(
        [
          '.card-wrapper',
          '.product-card',
          '[class*="product-card"]'
        ].join(',')
      )
      .forEach((card) => {
        register(
          card,
          'isekaid-scroll-card',
          'isekaid-entrance'
        );
      });

    /*
      Creator carousel cards.
    */
    scope
      .querySelectorAll('.isekaid-creator-card')
      .forEach((card) => {
        register(
          card,
          'isekaid-scroll-card',
          'isekaid-entrance'
        );
      });

    /*
      Creator carousel heading and track are kept separate.
      The horizontal track itself should not be translated,
      because it already manages its own horizontal scrolling.
    */
    scope
      .querySelectorAll(
        '.isekaid-creator-carousel__track'
      )
      .forEach((track) => {
        track.classList.add('isekaid-no-section-shift');
      });

    /*
      Product hotspot showcase.
    */
    scope
      .querySelectorAll(
        '.isekaid-hotspots__image-stage'
      )
      .forEach((imageStage) => {
        register(
          imageStage,
          'isekaid-scroll-hotspot-image',
          'isekaid-entrance-left'
        );
      });

    scope
      .querySelectorAll(
        '.isekaid-hotspots__panel'
      )
      .forEach((panel) => {
        register(
          panel,
          'isekaid-scroll-hotspot-panel',
          'isekaid-entrance-right'
        );
      });

    /*
      Reviews and Judge.me.
    */
    scope
      .querySelectorAll(
        [
          '.jdgm-widget',
          '.jdgm-rev-widg',
          '.jdgm-rev',
          '[class*="review-card"]',
          '[class*="testimonial"]'
        ].join(',')
      )
      .forEach((review) => {
        register(
          review,
          'isekaid-scroll-review',
          'isekaid-entrance'
        );
      });

    /*
      Merchant-added manual classes.
    */
    scope
      .querySelectorAll('.isekaid-manual-parallax')
      .forEach((element) => {
        register(element);
      });

    requestMotionUpdate();
  };

  /*
    Watch for dynamically inserted Judge.me content and sections
    reloaded by the Shopify editor.
  */
  const observeDynamicContent = () => {
    const mutationObserver = new MutationObserver(
      (mutations) => {
        let shouldDecorate = false;

        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (
              node.nodeType === Node.ELEMENT_NODE
            ) {
              shouldDecorate = true;
            }
          });
        });

        if (shouldDecorate) {
          decoratePage(document);
        }
      }
    );

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  const initialize = () => {
    if (systemStarted) {
      return;
    }

    systemStarted = true;

    document.documentElement.classList.add(
      READY_CLASS
    );

    decoratePage(document);

    window.addEventListener(
      'scroll',
      requestMotionUpdate,
      { passive: true }
    );

    window.addEventListener(
      'resize',
      requestMotionUpdate,
      { passive: true }
    );

    window.addEventListener(
      'orientationchange',
      requestMotionUpdate,
      { passive: true }
    );

    document.addEventListener(
      'shopify:section:load',
      (event) => {
        decoratePage(event.target);
      }
    );

    reducedMotion.addEventListener?.(
      'change',
      requestMotionUpdate
    );

    observeDynamicContent();
    requestMotionUpdate();
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }
})();